import { useReduxState } from 'hooks';
import { Alert, Button, ContentBox } from 'components/common';
import { ScormNavigation } from 'components/editor/blocks/scorm';
import { ReaderTaskHeader } from 'components/reader/common';
import {
  DEFAULT_MIN_HEIGHT,
  DEFAULT_EDITOR_WIDTH,
  DEFAULT_SCALE,
  DEFAULT_ASPECT_RATIO
} from './EditorBlockEmbedConstants';

import type { Block, LocalizeBlock } from 'shared-types';

type LocalizedData = LocalizeBlock<
  Extract<Block, { type: 'embedcode' | 'embedlink' | 'h5p' | 'scorm' }>
>['data'];

interface Props {
  actualWidth: number;
  blockId: Block['id'];
  data: LocalizedData;
  fontSize?: 'small' | 'medium' | 'large' | 'huge';
  handleFullscreen: () => void;
  handleModal?: () => void;
  iframeKey: string;
  isInModal?: boolean;
  isScorm: boolean;
  showHeader: boolean;
  src: string;
}

const EditorBlockEmbedIframeContent = ({
  actualWidth,
  blockId,
  data,
  fontSize = 'medium',
  handleFullscreen,
  handleModal,
  iframeKey,
  isInModal,
  isScorm,
  showHeader,
  src
}: Props) => {
  const { messages, theme } = useReduxState();

  const getEmbedPreviewWrapperStyle = () => {
    if (isInModal) return { minHeight: '95vh', paddingBottom: null };

    const aspectRatio = data.aspectRatio
      ? data.aspectRatio.value
      : DEFAULT_ASPECT_RATIO;

    const minHeight = data.minHeight ? data.minHeight : DEFAULT_MIN_HEIGHT;

    return {
      minHeight: data.useAspectRatio ? null : minHeight,
      paddingBottom: data.useAspectRatio ? aspectRatio * 100 + '%' : null
    };
  };

  const getIframeStyle = (): React.CSSProperties => {
    const editorWidth = data.editorIframeSetZoomWidth || DEFAULT_EDITOR_WIDTH;

    const aspectRatio = data.aspectRatio
      ? data.aspectRatio.value
      : DEFAULT_ASPECT_RATIO;

    const previousMinHeight = data.minHeight || DEFAULT_MIN_HEIGHT;

    let scale = data.scale || DEFAULT_SCALE;
    if (scale !== DEFAULT_SCALE && actualWidth < editorWidth) {
      const windowScale = actualWidth / editorWidth;
      scale = scale * windowScale;
    }

    let height: number, minHeight: number;
    const zoomLevel = 1 / scale;
    if (data.useAspectRatio) {
      // NOTE: must not use percentage height, it is always a percentage of container
      minHeight = undefined;
      height = zoomLevel * aspectRatio * actualWidth;
    } else {
      minHeight = zoomLevel * previousMinHeight;
      height = zoomLevel * previousMinHeight;
    }
    const width = zoomLevel * 100 + '%';

    const translate = 0 - (100 * (1 - scale)) / 2;
    const transform =
      `translate(${translate}%, ${translate}%)` + ` scale(${scale}, ${scale})`;

    return {
      background: '#FFFFFF',
      border: 0,
      minHeight: isInModal ? '100%' : minHeight,
      width: width,
      height: height,
      overflowX: 'hidden',
      WebkitTransform: transform,
      MozTransform: transform,
      OTransform: transform,
      msTransform: transform,
      transform: transform
    };
  };

  const handleForceFullScreen = () => {
    const el = document.getElementById(iframeKey);
    if (el.hidden) el.hidden = false;
    else el.hidden = true;

    handleFullscreen();
  };

  return data.enableFullScreen === 'forced' ? (
    <>
      <ContentBox size={fontSize || 'medium'}>
        {showHeader ? (
          <ReaderTaskHeader
            fontSize={fontSize}
            isMandatory={'isMandatory' in data ? data.isMandatory : false}
            messages={messages}
            theme={theme}
            title={messages.tasksEmbeddedTask}
          />
        ) : null}

        <div className="embed-fullscreen-forced">
          {data.openFullScreenMessage ? (
            <div className="embed-fullscreen-forced-description">
              {data.openFullScreenMessage}
            </div>
          ) : null}

          <div className="embed-fullscreen-forced-info">
            <Alert
              size={fontSize || 'medium'}
              text={messages.editorBlockEmbedFullscreenOnlyUserInfo}
              type="info-dark"
            />
          </div>

          <div className="embed-fullscreen-forced-button">
            <Button
              onClick={handleForceFullScreen}
              text={messages.editorBlockEmbedViewFullscreen}
              type="submit"
            />
          </div>

          <style jsx>{`
            .embed-fullscreen-forced-description {
              margin-bottom: 15px;
            }
            .embed-fullscreen-forced-info {
              margin-bottom: 15px;
            }
          `}</style>
        </div>
      </ContentBox>

      <iframe
        className="embed-iframe"
        hidden={true}
        id={iframeKey}
        key={iframeKey}
        loading="lazy"
        src={src}
        style={getIframeStyle()}
        title={`Embed block ${blockId}`}
      />
    </>
  ) : (
    <div className="embed-outer">
      {showHeader ? (
        <ReaderTaskHeader
          fontSize={fontSize}
          isMandatory={'isMandatory' in data ? data.isMandatory : false}
          messages={messages}
          theme={theme}
          style={{ padding: '10px 15px 5px' }}
          title={messages.tasksEmbeddedTask}
        />
      ) : null}

      <div className="embed-preview" style={getEmbedPreviewWrapperStyle()}>
        <iframe
          allowFullScreen={data.enableFullScreen === 'enabled'}
          className="embed-iframe"
          id={iframeKey}
          key={iframeKey}
          loading="lazy"
          src={src}
          style={getIframeStyle()}
          title={`Embed block ${blockId}`}
        />

        {isScorm && <ScormNavigation iframeKey={iframeKey} blockId={blockId} />}

        {data.enableFullScreen === 'enabled' && (
          <Button
            category="lighter"
            data-tooltip={
              isInModal
                ? messages.commonClose
                : messages.editorBlockEmbedFullscreen
            }
            icon={isInModal ? 'compress' : 'expand'}
            onClick={isInModal ? handleModal : handleFullscreen}
            size="huge"
            style={{
              position: 'absolute',
              right: 10,
              bottom: 10,
              width: '32px',
              height: '32px',
              padding: '0'
            }}
          />
        )}
      </div>

      <style jsx>{`
        .embed-outer {
          box-shadow: rgba(0, 0, 0, 0.05) 1px 1px 10px 0px;
        }
        .embed-preview {
          position: relative;
          background: rgba(0, 0, 0, 0.05);
          height: 100%;
          width: 100%;
          overflow: hidden;
        }
        .embed-preview .embed-iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: white;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockEmbedIframeContent;
